import { Injectable, OnDestroy, Renderer2, RendererFactory2 } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

/**
 * Global service to track keyboard navigation mode across the application.
 *
 * This service maintains a global state of whether the user is navigating
 * with keyboard (Tab key) or mouse. This is necessary for proper focus
 * outline management, especially when components are dynamically created
 * inside panels or overlays.
 */
@Injectable({
  providedIn: 'root',
})
export class KeyboardModeService implements OnDestroy {
  private readonly renderer: Renderer2 | undefined;
  private readonly keyboardModeEnabled$ = new BehaviorSubject<boolean>(false);

  private mouseDownListener: (() => void) | undefined;
  private keyDownListener: (() => void) | undefined;

  constructor(private rendererFactory: RendererFactory2) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
    this.setupGlobalListeners();
  }

  /**
   * Clean up global listeners (called on service destruction, though this is unlikely for root service)
   */
  ngOnDestroy(): void {
    if (this.mouseDownListener) {
      this.mouseDownListener();
    }
    if (this.keyDownListener) {
      this.keyDownListener();
    }
  }

  /**
   * Observable that emits true when keyboard navigation is active, false when mouse navigation is active
   */
  get isKeyboardMode$() {
    return this.keyboardModeEnabled$.asObservable();
  }

  /**
   * Get the current keyboard mode state synchronously
   */
  get isKeyboardMode(): boolean {
    return this.keyboardModeEnabled$.value;
  }

  /**
   * Force set keyboard mode (useful for testing or specific scenarios)
   */
  setKeyboardMode(enabled: boolean): void {
    this.keyboardModeEnabled$.next(enabled);
  }

  /**
   * Set up global document listeners to track input modality
   */
  private setupGlobalListeners(): void {
    // Listen for mousedown on document to detect mouse interaction
    this.mouseDownListener = this.renderer.listen('document', 'mousedown', () => {
      if (this.keyboardModeEnabled$.value) {
        this.keyboardModeEnabled$.next(false);
      }
    });

    // Listen for keydown (Tab or Shift+Tab) on document to detect keyboard interaction
    this.keyDownListener = this.renderer.listen('document', 'keydown', (event: KeyboardEvent) => {
      if (event.key === 'Tab' && !this.keyboardModeEnabled$.value) {
        this.keyboardModeEnabled$.next(true);
      }
    });
  }
}
